// The functions the app calls. Nothing above this layer knows a URL.

import type {
  ChatEvent,
  ConversationDetailsDto,
  ConversationDto,
  CreateProjectDto,
  MeDto,
  ProjectDetailsDto,
  PreviewDto,
  SuggestionDto,
  ProjectCreationDto,
  ProjectDto,
  PublishDto,
} from "../types/index.ts";
import { apiFetch, apiJson } from "./http.ts";
import { parseSse } from "./sse.ts";

export const fetchMe = () => apiJson<MeDto>("/v1/me");

export const fetchProjects = () => apiJson<ProjectDto[]>("/v1/projects");

/** Starts the Sortera Factory for a new project, as the signed-in person. */
export const createProject = (input: CreateProjectDto) =>
  apiJson<ProjectCreationDto>("/v1/projects", { method: "POST", body: JSON.stringify(input) });

/** How a project being made is getting on. */
export const fetchProjectCreation = (id: string) => apiJson<ProjectCreationDto>(`/v1/project-creations/${encodeURIComponent(id)}`);

export const fetchProject = (repo: string) => apiJson<ProjectDetailsDto>(`/v1/projects/${encodeURIComponent(repo)}`);

/** "Lägg upp i testmiljön": the person's saved work goes to the test environment. */
export const publishProject = (repo: string) =>
  apiJson<PublishDto>(`/v1/projects/${encodeURIComponent(repo)}/publish`, { method: "POST" });

/** The latest preview of the project's frontend, or null before one has been built. */
export const fetchPreview = (repo: string) =>
  apiJson<PreviewDto | null>(`/v1/projects/${encodeURIComponent(repo)}/preview`);

/** Three first messages picked for the project, or null when there are none to pick from. */
export const fetchSuggestions = (repo: string) =>
  apiJson<SuggestionDto[] | null>(`/v1/projects/${encodeURIComponent(repo)}/suggestions`);

export const fetchConversations = (repo: string) =>
  apiJson<ConversationDto[]>(`/v1/conversations?repo=${encodeURIComponent(repo)}`);

export const fetchConversation = (id: string) => apiJson<ConversationDetailsDto>(`/v1/conversations/${id}`);

export const createConversation = (repo: string) =>
  apiJson<ConversationDto>("/v1/conversations", { method: "POST", body: JSON.stringify({ repo }) });

export const renameConversation = (id: string, title: string) =>
  apiJson<{ ok: true }>(`/v1/conversations/${id}`, { method: "PATCH", body: JSON.stringify({ title }) });

export const deleteConversation = (id: string) =>
  apiJson<{ ok: true }>(`/v1/conversations/${id}`, { method: "DELETE" });

export const stopTurn = (id: string) => apiJson<{ stopped: boolean }>(`/v1/conversations/${id}/stop`, { method: "POST" });

/** Sends a message and yields the turn's events until it is stored. */
export async function* sendMessage(id: string, text: string, model: string | null, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
  const response = await apiFetch(`/v1/conversations/${id}/messages`, {
    method: "POST",
    body: JSON.stringify({ text, model: model ?? undefined }),
    signal,
  });
  if (response.body) yield* parseSse<ChatEvent>(response.body);
}

/** Re-attaches to a turn still running — after a reload. Yields nothing if there is none. */
export async function* followTurn(id: string, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
  const response = await apiFetch(`/v1/conversations/${id}/events`, { signal });
  if (response.status === 204 || !response.body) return;
  yield* parseSse<ChatEvent>(response.body);
}
